import { Type } from "@sinclair/typebox";
import { IdSchema, Sha256Schema } from "./shared.js";
const PresentationTitleSchema = Type.Optional(Type.String({ minLength: 1, maxLength: 300 }));
const PresentationLabelSchema = Type.String({ minLength: 1, maxLength: 200 });
const PresentationUnitSchema = Type.Optional(Type.String({ minLength: 1, maxLength: 64 }));
/**
 * 数据 JSON Pointer。空字符串虽然是合法 RFC 6901 根指针，但展示合同必须指向明确字段，
 * 避免把整个不可信结果误当作一个可视值。
 */
export const PresentationJsonPointerSchema = Type.String({
    minLength: 1,
    maxLength: 512,
    pattern: "^/",
});
const PresentationColumnSchema = Type.Object({
    key: IdSchema,
    path: PresentationJsonPointerSchema,
    label: PresentationLabelSchema,
    unit: PresentationUnitSchema,
}, { additionalProperties: false });
const PresentationMetricSchema = Type.Object({
    presentationId: IdSchema,
    kind: Type.Literal("metrics"),
    title: PresentationTitleSchema,
    items: Type.Array(Type.Object({
        sourcePath: PresentationJsonPointerSchema,
        label: PresentationLabelSchema,
        unit: PresentationUnitSchema,
    }, { additionalProperties: false }), { minItems: 1, maxItems: 16 }),
}, { additionalProperties: false });
const PresentationTableSchema = Type.Object({
    presentationId: IdSchema,
    kind: Type.Literal("table"),
    title: PresentationTitleSchema,
    sourcePath: PresentationJsonPointerSchema,
    columns: Type.Array(PresentationColumnSchema, { minItems: 1, maxItems: 12 }),
    linkKeyPath: Type.Optional(PresentationJsonPointerSchema),
}, { additionalProperties: false });
const PresentationChartSchema = Type.Object({
    presentationId: IdSchema,
    kind: Type.Literal("chart"),
    title: PresentationTitleSchema,
    sourcePath: PresentationJsonPointerSchema,
    chartType: Type.Union([Type.Literal("bar"), Type.Literal("line"), Type.Literal("scatter")]),
    xAxis: Type.Object({
        path: Type.Optional(PresentationJsonPointerSchema),
        label: PresentationLabelSchema,
        unit: PresentationUnitSchema,
    }, { additionalProperties: false }),
    series: Type.Array(PresentationColumnSchema, { minItems: 1, maxItems: 8 }),
    linkKeyPath: Type.Optional(PresentationJsonPointerSchema),
}, { additionalProperties: false });
const PresentationSequenceBase = {
    presentationId: IdSchema,
    kind: Type.Literal("sequence"),
    title: PresentationTitleSchema,
    sourcePath: PresentationJsonPointerSchema,
    alphabet: Type.Union([
        Type.Literal("rna"),
        Type.Literal("dna"),
        Type.Literal("nucleic_acid"),
        Type.Literal("protein"),
    ]),
};
const PresentationSingleSequenceSchema = Type.Object({
    ...PresentationSequenceBase,
    shape: Type.Literal("single"),
    label: Type.Optional(PresentationLabelSchema),
}, { additionalProperties: false });
const PresentationScalarSequenceListSchema = Type.Object({
    ...PresentationSequenceBase,
    shape: Type.Literal("scalar_list"),
    labelPrefix: Type.Optional(PresentationLabelSchema),
}, { additionalProperties: false });
const PresentationRecordSequenceListSchema = Type.Object({
    ...PresentationSequenceBase,
    shape: Type.Literal("record_list"),
    sequencePath: PresentationJsonPointerSchema,
    labelPath: Type.Optional(PresentationJsonPointerSchema),
    linkKeyPath: Type.Optional(PresentationJsonPointerSchema),
}, { additionalProperties: false });
const PresentationMatrixBase = {
    presentationId: IdSchema,
    kind: Type.Literal("matrix"),
    title: PresentationTitleSchema,
    sourcePath: PresentationJsonPointerSchema,
};
const PresentationRecordMatrixSchema = Type.Object({
    ...PresentationMatrixBase,
    shape: Type.Literal("records"),
    columns: Type.Array(PresentationColumnSchema, { minItems: 2, maxItems: 24 }),
    rowLabelPath: Type.Optional(PresentationJsonPointerSchema),
    linkKeyPath: Type.Optional(PresentationJsonPointerSchema),
}, { additionalProperties: false });
const PresentationNumericRowsMatrixSchema = Type.Object({
    ...PresentationMatrixBase,
    shape: Type.Literal("numeric_rows"),
    columnLabels: Type.Optional(Type.Array(PresentationLabelSchema, { minItems: 2, maxItems: 24 })),
}, { additionalProperties: false });
const PresentationArtifactGroupSchema = Type.Object({
    presentationId: IdSchema,
    kind: Type.Literal("artifact_group"),
    title: Type.String({ minLength: 1, maxLength: 200 }),
    sourcePath: PresentationJsonPointerSchema,
    mediaTypes: Type.Optional(Type.Array(Type.String({ minLength: 1, maxLength: 255 }), {
        minItems: 1,
        maxItems: 32,
    })),
}, { additionalProperties: false });
export const PresentationViewSchema = Type.Union([
    PresentationMetricSchema,
    PresentationTableSchema,
    PresentationChartSchema,
    PresentationSingleSequenceSchema,
    PresentationScalarSequenceListSchema,
    PresentationRecordSequenceListSchema,
    PresentationRecordMatrixSchema,
    PresentationNumericRowsMatrixSchema,
    PresentationArtifactGroupSchema,
]);
export const PresentationContractSchema = Type.Object({
    version: Type.Literal(1),
    outputSchemaDigest: Sha256Schema,
    views: Type.Array(PresentationViewSchema, { minItems: 1, maxItems: 16 }),
}, { additionalProperties: false });
export const PresentationSchemaIssueCodeSchema = Type.Union([
    Type.Literal("output_schema_digest_mismatch"),
    Type.Literal("duplicate_identifier"),
    Type.Literal("schema_path_mismatch"),
    Type.Literal("schema_shape_mismatch"),
]);
export const PresentationSnapshotSchema = Type.Object({
    contract: PresentationContractSchema,
    contractDigest: Sha256Schema,
    functionSchemaDigest: Sha256Schema,
    schemaIssues: Type.Array(Type.Object({
        presentationId: IdSchema,
        code: PresentationSchemaIssueCodeSchema,
    }, { additionalProperties: false }), { maxItems: 16 }),
}, { additionalProperties: false });
//# sourceMappingURL=presentation.js.map